import { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";

// Pages
import HomePage from "./pages/HomePage";
import ApartmentSearchPage from "./pages/ApartmentSearchPage";
import NeighborhoodsPage from "./pages/NeighborhoodsPage";
import HiddenSpecialsPage from "./pages/HiddenSpecialsPage";
import InvestorFunnelPage from "./pages/InvestorFunnelPage";
import ContactPage from "./pages/ContactPage";
import LoginPage from "./pages/LoginPage";
import BookRedirect from "./pages/BookRedirect";
import AuthCallback from "./pages/AuthCallback";
import InstantResultsPage from "./pages/InstantResultsPage";
import HoustonApartmentFinder from "./pages/HoustonApartmentFinder";
import DealConciergePage from "./pages/DealConciergePage";
import QuizPage from "./pages/QuizPage";
import MatchQuizPage from "./pages/MatchQuizPage";
import UnqApartmentSearch from "./pages/UnqApartmentSearch";
import UnqRelocationPage from "./pages/UnqRelocationPage";
import NeighborhoodPage from "./pages/NeighborhoodPage";
import BlogIndex from "./pages/BlogIndex";
import BlogArticle from "./pages/BlogArticle";
const FunnelPerformance = lazy(() => import("./pages/admin/FunnelPerformance"));
import { initAnalytics } from "./lib/analytics";
import { initAttribution, trackPageView } from "./lib/attribution";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminLeads from "./pages/admin/Leads";
import AdminTemplates from "./pages/admin/Templates";
import AdminCampaigns from "./pages/admin/Campaigns";
import AdminContent from "./pages/admin/Content";
import AdminUsers from "./pages/admin/Users";
import DealInventory from "./pages/admin/DealInventory";
import TopPicks from "./pages/admin/TopPicks";
import Attribution from "./pages/admin/Attribution";
import DripDashboard from "./pages/admin/DripDashboard";
import DealIntelligence from "./pages/admin/DealIntelligence";
import LeaseDashboard from "./pages/admin/LeaseDashboard";
import TourAvailability from "./pages/admin/TourAvailability";
import PriorityQueue from "./pages/admin/PriorityQueue";
import PartnersCompanies from "./pages/admin/PartnersCompanies";
import PartnerCompanyDetail from "./pages/admin/PartnerCompanyDetail";
import PartnersDashboard from "./pages/admin/PartnersDashboard";
import PartnerPortal from "./pages/PartnerPortal";
import GetStartedWizard from "./pages/GetStartedWizard";
import GetStartedThankYou from "./pages/GetStartedThankYou";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import AdminLayout from "./components/AdminLayout";
import StickyContactButton from "./components/StickyContactButton";
import { AssistantPanel } from "./components/assistant/AssistantPanel";
import { Toaster } from "@/components/ui/toaster";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // If returning from the OAuth callback, AuthCallback exchanges the
      // session_id and establishes the session first — skip the /me check.
      if (window.location.hash?.includes("session_id=")) {
        setLoading(false);
        return;
      }
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // Used by the Google OAuth callback (token already obtained from backend)
  const loginWithToken = (access_token, userData) => {
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
  };

  const logout = () => {
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, loginWithToken, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public Layout
const PublicLayout = ({ children }) => {
  const location = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  if (isAdminRoute) return children;
  
  return (
    <>
      <Navbar />
      <main className="min-h-screen">{children}</main>
      <Footer />
      <AssistantPanel open={chatOpen} onClose={() => setChatOpen(false)} />
      <StickyContactButton onOpenChat={() => setChatOpen(true)} />
    </>
  );
};

function AppRoutes() {
  const location = useLocation();
  // Emergent OAuth callback: session_id arrives in the URL fragment.
  // Must be detected synchronously during render (before ProtectedRoute runs).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/search" element={<PublicLayout><ApartmentSearchPage /></PublicLayout>} />
      <Route path="/instant-results" element={<PublicLayout><InstantResultsPage /></PublicLayout>} />
      <Route path="/neighborhoods" element={<PublicLayout><NeighborhoodsPage /></PublicLayout>} />
      <Route path="/specials" element={<PublicLayout><HiddenSpecialsPage /></PublicLayout>} />
      <Route path="/investors" element={<PublicLayout><InvestorFunnelPage /></PublicLayout>} />
      <Route path="/off-market" element={<PublicLayout><InvestorFunnelPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/book" element={<BookRedirect />} />
      <Route path="/houston-apartments" element={<PublicLayout><HoustonApartmentFinder /></PublicLayout>} />
      <Route path="/apply" element={<PublicLayout><DealConciergePage /></PublicLayout>} />
      <Route path="/quiz" element={<PublicLayout><MatchQuizPage /></PublicLayout>} />
      <Route path="/find-apartment" element={<PublicLayout><QuizPage /></PublicLayout>} />
      <Route path="/apartment-search" element={<PublicLayout><UnqApartmentSearch variant="general" /></PublicLayout>} />
      <Route path="/luxury-apartments" element={<PublicLayout><UnqApartmentSearch variant="luxury" /></PublicLayout>} />
      <Route path="/homes-for-rent" element={<PublicLayout><UnqApartmentSearch variant="homes" /></PublicLayout>} />
      <Route path="/relocation-services" element={<PublicLayout><UnqRelocationPage /></PublicLayout>} />
      <Route path="/apartments/:slug" element={<PublicLayout><NeighborhoodPage /></PublicLayout>} />
      <Route path="/blog" element={<PublicLayout><BlogIndex /></PublicLayout>} />
      <Route path="/blog/:slug" element={<PublicLayout><BlogArticle /></PublicLayout>} />
      <Route path="/get-started" element={<GetStartedWizard />} />
      <Route path="/get-started/thank-you" element={<GetStartedThankYou />} />
      <Route path="/partner-portal/:token" element={<PartnerPortal />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminLayout><AdminDashboard /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/leads" element={
        <ProtectedRoute>
          <AdminLayout><AdminLeads /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/templates" element={
        <ProtectedRoute>
          <AdminLayout><AdminTemplates /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/campaigns" element={
        <ProtectedRoute>
          <AdminLayout><AdminCampaigns /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/content" element={
        <ProtectedRoute>
          <AdminLayout><AdminContent /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <AdminLayout><AdminUsers /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/deals" element={
        <ProtectedRoute>
          <AdminLayout><DealInventory /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/top-picks" element={
        <ProtectedRoute>
          <AdminLayout><TopPicks /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/attribution" element={
        <ProtectedRoute>
          <AdminLayout><Attribution /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/drip" element={
        <ProtectedRoute>
          <AdminLayout><DripDashboard /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/intelligence" element={
        <ProtectedRoute>
          <AdminLayout><DealIntelligence /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/funnel" element={
        <ProtectedRoute>
          <AdminLayout>
            <Suspense fallback={<div className="p-8 text-sm text-slate-400">Loading funnel analytics…</div>}>
              <FunnelPerformance />
            </Suspense>
          </AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/leases" element={
        <ProtectedRoute>
          <AdminLayout><LeaseDashboard /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/tour-availability" element={
        <ProtectedRoute>
          <AdminLayout><TourAvailability /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/priority-queue" element={
        <ProtectedRoute>
          <AdminLayout><PriorityQueue /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/partners" element={
        <ProtectedRoute>
          <AdminLayout><PartnersCompanies /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/partners/dashboard" element={
        <ProtectedRoute>
          <AdminLayout><PartnersDashboard /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/partners/:id" element={
        <ProtectedRoute>
          <AdminLayout><PartnerCompanyDetail /></AdminLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function RouteAttributionTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    initAttribution();
    initAnalytics();
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteAttributionTracker />
        <AppRoutes />
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
